import { useState } from 'react'
import { useSession } from '../lib/session'
import { usePlans, useRefreshingMutation } from '../lib/queries'
import { api } from '../lib/api'
import type { Plan } from '../../shared/types'
import { Avatar, Empty, ErrorText, Loading, Pill, Sheet } from '../components/ui'
import { PlusIcon } from '../components/Icons'

const KIND_LABELS: Record<Plan['kind'], string> = {
  going_out: 'Going out',
  friends_over: 'Friends over',
  activity: 'Activity',
  other: 'Other',
}

const STATUS_TONE: Record<Plan['status'], 'ok' | 'warn' | 'danger' | 'default'> = {
  approved: 'ok',
  requested: 'warn',
  declined: 'danger',
  cancelled: 'default',
}

export function Plans() {
  const { isParent, session } = useSession()
  const [scope, setScope] = useState<'upcoming' | 'all'>('upcoming')
  const { data, isLoading } = usePlans(scope)
  const [askOpen, setAskOpen] = useState(false)
  const [deciding, setDeciding] = useState<Plan | null>(null)

  const cancel = useRefreshingMutation((id: string) => api.cancelPlan(id))

  if (isLoading) return <Loading />

  const plans = data ?? []
  const pending = plans.filter((plan) => plan.status === 'requested')
  const rest = plans.filter((plan) => plan.status !== 'requested')

  return (
    <div className="page">
      <div className="row-between">
        <h1 className="title">Plans</h1>
        <button type="button" className="btn btn-primary" onClick={() => setAskOpen(true)}>
          <PlusIcon /> {isParent ? 'Add' : 'Ask'}
        </button>
      </div>

      <p className="subtitle">
        Communicate extra-curricular plans ahead of time — going out with friends, having friends over, and so
        on.
      </p>

      {pending.length > 0 ? (
        <section className="card">
          <div className="row-between">
            <h2 className="title">Waiting on a parent</h2>
            <Pill tone="warn">{pending.length}</Pill>
          </div>
          <div className="list">
            {pending.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                canDecide={isParent}
                canCancel={plan.memberId === session?.memberId || isParent}
                onDecide={() => setDeciding(plan)}
                onCancel={() => cancel.mutate(plan.id)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="card">
        <div className="row-between">
          <h2 className="title">{scope === 'all' ? 'Everything' : 'Coming up'}</h2>
          <button
            type="button"
            className="btn btn-quiet"
            onClick={() => setScope(scope === 'all' ? 'upcoming' : 'all')}
          >
            {scope === 'all' ? 'Show upcoming' : 'Show all'}
          </button>
        </div>
        {rest.length === 0 ? (
          <Empty title="Nothing here yet" hint="Plans you tell us about ahead of time show up here." />
        ) : (
          <div className="list">
            {rest.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                canDecide={false}
                canCancel={
                  plan.status !== 'cancelled' && (plan.memberId === session?.memberId || isParent)
                }
                onDecide={() => setDeciding(plan)}
                onCancel={() => cancel.mutate(plan.id)}
              />
            ))}
          </div>
        )}
      </section>

      <ErrorText error={cancel.error} />

      <AskSheet open={askOpen} onClose={() => setAskOpen(false)} />
      <DecideSheet plan={deciding} onClose={() => setDeciding(null)} />
    </div>
  )
}

function PlanCard({
  plan,
  canDecide,
  canCancel,
  onDecide,
  onCancel,
}: {
  plan: Plan
  canDecide: boolean
  canCancel: boolean
  onDecide: () => void
  onCancel: () => void
}) {
  return (
    <div className="card card-tight card-flat" style={{ border: '1px solid var(--border)' }}>
      <div className="row-between">
        <div className="row">
          <Avatar emoji={plan.memberEmoji} size="sm" />
          <div>
            <div style={{ fontWeight: 700 }}>{plan.title}</div>
            <div className="faint">
              {plan.memberName} · {KIND_LABELS[plan.kind]}
            </div>
          </div>
        </div>
        <Pill tone={STATUS_TONE[plan.status]}>{plan.status}</Pill>
      </div>

      <div className="faint">
        {formatWhen(plan.startsAt)}
        {plan.endsAt ? ` – ${formatWhen(plan.endsAt)}` : ''}
      </div>

      {plan.details ? <p className="subtitle">{plan.details}</p> : null}
      {plan.decisionNote ? <p className="subtitle">Note: {plan.decisionNote}</p> : null}

      {canDecide || canCancel ? (
        <div className="row">
          {canDecide ? (
            <button type="button" className="btn btn-primary grow" onClick={onDecide}>
              Decide
            </button>
          ) : null}
          {canCancel ? (
            <button type="button" className="btn btn-quiet" onClick={onCancel}>
              Cancel
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function formatWhen(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function defaultStart(): string {
  const date = new Date()
  date.setMinutes(0, 0, 0)
  date.setHours(date.getHours() + 2)
  // datetime-local wants a local-time string with no timezone suffix.
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function AskSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { isParent } = useSession()
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [kind, setKind] = useState<Plan['kind']>('going_out')
  const [startsAt, setStartsAt] = useState(defaultStart)
  const [endsAt, setEndsAt] = useState('')

  const create = useRefreshingMutation(() =>
    api.createPlan({
      title: title.trim(),
      details: details.trim() || null,
      kind,
      startsAt,
      endsAt: endsAt || null,
    }),
  )

  return (
    <Sheet open={open} title={isParent ? 'Add a plan' : 'Ask about a plan'} onClose={onClose}>
      <div className="field">
        <label className="label" htmlFor="plan-title">
          What’s the plan?
        </label>
        <input
          id="plan-title"
          className="input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Movies with Sam and Alex"
        />
      </div>

      <div className="field">
        <span className="label">Type</span>
        <div className="chips">
          {(Object.keys(KIND_LABELS) as Plan['kind'][]).map((option) => (
            <button
              key={option}
              type="button"
              className={`chip${kind === option ? ' chip-on' : ''}`}
              onClick={() => setKind(option)}
            >
              {KIND_LABELS[option]}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="plan-start">
          Starts
        </label>
        <input
          id="plan-start"
          className="input"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="plan-end">
          Back by
        </label>
        <input
          id="plan-end"
          className="input"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="plan-details">
          Anything else?
        </label>
        <textarea
          id="plan-details"
          className="textarea"
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder="Who's going, how you're getting there and back"
        />
      </div>

      <ErrorText error={create.error} />
      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!title.trim() || !startsAt || create.isPending}
        onClick={() =>
          create.mutate(undefined, {
            onSuccess: () => {
              setTitle('')
              setDetails('')
              setEndsAt('')
              onClose()
            },
          })
        }
      >
        {isParent ? 'Add plan' : 'Send to a parent'}
      </button>
    </Sheet>
  )
}

function DecideSheet({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const [note, setNote] = useState('')
  const decide = useRefreshingMutation(
    ({ id, status }: { id: string; status: 'approved' | 'declined' }) =>
      api.decidePlan(id, status, note.trim() || null),
  )

  return (
    <Sheet open={plan !== null} title={plan?.title ?? 'Plan'} onClose={onClose}>
      {plan ? (
        <>
          <p className="subtitle">
            {plan.memberName} · {formatWhen(plan.startsAt)}
            {plan.endsAt ? ` – ${formatWhen(plan.endsAt)}` : ''}
          </p>
          {plan.details ? <p className="subtitle">{plan.details}</p> : null}
          <div className="field">
            <label className="label" htmlFor="decide-note">
              Note back
            </label>
            <input
              id="decide-note"
              className="input"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Home by 10, text when you're leaving"
            />
          </div>
          <ErrorText error={decide.error} />
          <div className="row">
            <button
              type="button"
              className="btn btn-ok grow btn-lg"
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate(
                  { id: plan.id, status: 'approved' },
                  {
                    onSuccess: () => {
                      setNote('')
                      onClose()
                    },
                  },
                )
              }
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-danger grow btn-lg"
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate(
                  { id: plan.id, status: 'declined' },
                  {
                    onSuccess: () => {
                      setNote('')
                      onClose()
                    },
                  },
                )
              }
            >
              Not this time
            </button>
          </div>
        </>
      ) : null}
    </Sheet>
  )
}
